import { FormlyFieldConfig } from "@ngx-formly/core";

/**
 * Form for creating Control Lots
 */
export class AdminCreateLotForm {
  fields: FormlyFieldConfig[];
  actionText = 'button.create';

  constructor(translateService, dataProjects, dataConcretes) {
    const projects = dataProjects.map(p => ({ value: p._id, label: p.name }));
    const concretes = dataConcretes.map(c => ({ value: c._id, label: c.name }));

    this.fields = [
      {
        key: 'code',
        type: 'input',
        wrappers: ['default-wrapper'],
        props: { required: true },
        expressions: { 'props.label': translateService.stream('fields.code') }
      },
      {
        key: 'project',
        type: 'select',
        wrappers: ['default-wrapper'],
        props: { options: projects, required: true },
        expressions: { 'props.label': translateService.stream('fields.project') }
      },
      {
        key: 'concrete',
        type: 'select',
        wrappers: ['default-wrapper'],
        props: { options: concretes, required: true },
        expressions: { 'props.label': translateService.stream('fields.concrete') }
      },
      {
        key: 'structuralElementType',
        type: 'input',
        wrappers: ['default-wrapper'],
        props: { required: true },
        expressions: { 'props.label': translateService.stream('fields.structuralElementType') }
      },
      {
        key: 'elementSetId',
        type: 'input',
        wrappers: ['default-wrapper'],
        props: { required: true },
        expressions: { 'props.label': translateService.stream('fields.elementSetId') }
      },
      {
        key: 'startDate',
        type: 'datepicker', // Assuming a datepicker type exists or fallback to input[type=date]
        wrappers: ['default-wrapper'],
        props: { type: 'date' },
        expressions: { 'props.label': translateService.stream('fields.startDate') }
      }
    ];
  }
}

/**
 * Form for editing Control Lots
 */
export class AdminEditLotForm {
  fields: FormlyFieldConfig[];
  actionText = 'button.save';

  constructor(translateService, currentValue, dataProjects, dataConcretes) {
    const projects = dataProjects.map(p => ({ value: p._id, label: p.name }));
    const concretes = dataConcretes.map(c => ({ value: c._id, label: c.name }));

    this.fields = [
      {
        key: 'code',
        type: 'input',
        wrappers: ['default-wrapper'],
        defaultValue: currentValue.code,
        props: { required: true },
        expressions: { 'props.label': translateService.stream('fields.code') }
      },
      {
        key: 'project',
        type: 'select',
        wrappers: ['default-wrapper'],
        defaultValue: currentValue.project?._id || currentValue.project,
        props: { options: projects, required: true },
        expressions: { 'props.label': translateService.stream('fields.project') }
      },
      {
        key: 'concrete',
        type: 'select',
        wrappers: ['default-wrapper'],
        defaultValue: currentValue.concrete?._id || currentValue.concrete,
        props: { options: concretes, required: true },
        expressions: { 'props.label': translateService.stream('fields.concrete') }
      },
      {
        key: 'status',
        type: 'select',
        wrappers: ['default-wrapper'],
        defaultValue: currentValue.status,
        props: {
          options: [
            { label: 'OPEN', value: 'OPEN' },
            { label: 'CLOSED', value: 'CLOSED' },
            { label: 'ACCEPTED', value: 'ACCEPTED' },
            { label: 'REJECTED', value: 'REJECTED' }
          ]
        },
        expressions: { 'props.label': translateService.stream('fields.status') }
      },
      {
        key: 'structuralElementType',
        type: 'input',
        wrappers: ['default-wrapper'],
        defaultValue: currentValue.structuralElementType,
        props: { required: true },
        expressions: { 'props.label': translateService.stream('fields.structuralElementType') }
      },
      {
        key: 'startDate',
        type: 'input',
        wrappers: ['default-wrapper'],
        defaultValue: currentValue.startDate ? new Date(currentValue.startDate).toISOString().slice(0, 10) : null,
        props: { type: 'date' },
        expressions: { 'props.label': translateService.stream('fields.startDate') }
      },
      {
        key: 'closeDate',
        type: 'input',
        wrappers: ['default-wrapper'],
        defaultValue: currentValue.closeDate ? new Date(currentValue.closeDate).toISOString().slice(0, 10) : null,
        props: { type: 'date' },
        expressions: { 'props.label': translateService.stream('fields.closeDate') }
      }
    ];
  }
}

/**
 * Form for creating Concrete Pours
 */
export class AdminCreatePourForm {
  fields: FormlyFieldConfig[];
  actionText = 'button.create';

  constructor(translateService, dataProjects, dataConcretes) {
    const projects = dataProjects.map(p => ({ value: p._id, label: p.name }));
    const concretes = dataConcretes.map(c => ({ value: c._id, label: c.name }));

    this.fields = [
      {
        key: 'date',
        type: 'input',
        wrappers: ['default-wrapper'],
        props: { type: 'date', required: true },
        expressions: { 'props.label': translateService.stream('fields.date') }
      },
      {
        key: 'volume',
        type: 'input',
        wrappers: ['default-wrapper'],
        props: { type: 'number', required: true, step: 0.1 },
        expressions: { 'props.label': translateService.stream('fields.volume') }
      },
      {
        key: 'project',
        type: 'select',
        wrappers: ['default-wrapper'],
        props: { options: projects, required: true },
        expressions: { 'props.label': translateService.stream('fields.project') }
      },
      {
        key: 'concrete',
        type: 'select',
        wrappers: ['default-wrapper'],
        props: { options: concretes, required: true },
        expressions: { 'props.label': translateService.stream('fields.concrete') }
      },
      {
        key: 'structuralElementType',
        type: 'input',
        wrappers: ['default-wrapper'],
        props: { required: true },
        expressions: { 'props.label': translateService.stream('fields.structuralElementType') }
      },
      {
        key: 'elementSetId',
        type: 'input', // UUID or ref
        wrappers: ['default-wrapper'],
        expressions: { 'props.label': translateService.stream('fields.elementSetId') }
      }
    ];
  }
}

/**
 * Form for editing Concrete Pours
 */
export class AdminEditPourForm {
  fields: FormlyFieldConfig[];
  actionText = 'button.save';

  constructor(translateService, currentValue, dataProjects, dataConcretes) {
    const projects = dataProjects.map(p => ({ value: p._id, label: p.name }));
    const concretes = dataConcretes.map(c => ({ value: c._id, label: c.name }));

    this.fields = [
      {
        key: 'date',
        type: 'input',
        wrappers: ['default-wrapper'],
        defaultValue: currentValue.date ? new Date(currentValue.date).toISOString().slice(0, 10) : null,
        props: { type: 'date', required: true },
        expressions: { 'props.label': translateService.stream('fields.date') }
      },
      {
        key: 'volume',
        type: 'input',
        wrappers: ['default-wrapper'],
        defaultValue: currentValue.volume,
        props: { type: 'number', required: true, step: 0.1 },
        expressions: { 'props.label': translateService.stream('fields.volume') }
      },
      {
        key: 'project',
        type: 'select',
        wrappers: ['default-wrapper'],
        defaultValue: currentValue.project?._id || currentValue.project,
        props: { options: projects, required: true },
        expressions: { 'props.label': translateService.stream('fields.project') }
      },
      {
        key: 'concrete',
        type: 'select',
        wrappers: ['default-wrapper'],
        defaultValue: currentValue.concrete?._id || currentValue.concrete,
        props: { options: concretes, required: true },
        expressions: { 'props.label': translateService.stream('fields.concrete') }
      },
      {
        key: 'structuralElementType',
        type: 'input',
        wrappers: ['default-wrapper'],
        defaultValue: currentValue.structuralElementType,
        props: { required: true },
        expressions: { 'props.label': translateService.stream('fields.structuralElementType') }
      }
    ];
  }
}

/**
 * Form for creating Strength Test Results (Usually imported, but CRUD might be needed)
 */
export class AdminCreateTestResultForm {
  fields: FormlyFieldConfig[];
  actionText = 'button.create';

  constructor(translateService, dataProjects, dataConcretes) {
    const projects = dataProjects.map(p => ({ value: p._id, label: p.name }));
    const concretes = dataConcretes.map(c => ({ value: c._id, label: c.name }));

    this.fields = [
      {
        key: 'code',
        type: 'input',
        wrappers: ['default-wrapper'],
        props: { required: true },
        expressions: { 'props.label': translateService.stream('fields.code') }
      },
      {
        key: 'testDate',
        type: 'input',
        wrappers: ['default-wrapper'],
        props: { type: 'date', required: true },
        expressions: { 'props.label': translateService.stream('fields.date') }
      },
      {
        key: 'value',
        type: 'input',
        wrappers: ['default-wrapper'],
        props: { type: 'number', required: true, step: 0.1 },
        expressions: { 'props.label': translateService.stream('fields.value') }
      },
      {
        key: 'age',
        type: 'input',
        wrappers: ['default-wrapper'],
        props: { type: 'number', required: true },
        expressions: { 'props.label': translateService.stream('fields.age') }
      },
      {
        key: 'unit',
        type: 'input',
        wrappers: ['default-wrapper'],
        defaultValue: 'MPa',
        props: { required: true },
        expressions: { 'props.label': translateService.stream('fields.unit') }
      },
      {
        key: 'project',
        type: 'select',
        wrappers: ['default-wrapper'],
        props: { options: projects, required: true },
        expressions: { 'props.label': translateService.stream('fields.project') }
      },
      {
        key: 'concrete',
        type: 'select',
        wrappers: ['default-wrapper'],
        props: { options: concretes, required: true },
        expressions: { 'props.label': translateService.stream('fields.concrete') }
      }
    ];
  }
}

/**
 * Form for editing Strength Test Results
 */
export class AdminEditTestResultForm {
  fields: FormlyFieldConfig[];
  actionText = 'button.save';

  constructor(translateService, currentValue, dataProjects, dataConcretes) {
    const projects = dataProjects.map(p => ({ value: p._id, label: p.name }));
    const concretes = dataConcretes.map(c => ({ value: c._id, label: c.name }));

    this.fields = [
      {
        key: 'code',
        type: 'input',
        wrappers: ['default-wrapper'],
        defaultValue: currentValue.code,
        props: { required: true },
        expressions: { 'props.label': translateService.stream('fields.code') }
      },
      {
        key: 'testDate',
        type: 'input',
        wrappers: ['default-wrapper'],
        defaultValue: currentValue.testDate ? new Date(currentValue.testDate).toISOString().slice(0, 10) : null,
        props: { type: 'date', required: true },
        expressions: { 'props.label': translateService.stream('fields.date') }
      },
      {
        key: 'value',
        type: 'input',
        wrappers: ['default-wrapper'],
        defaultValue: currentValue.value,
        props: { type: 'number', required: true, step: 0.1 },
        expressions: { 'props.label': translateService.stream('fields.value') }
      },
      {
        key: 'age',
        type: 'input',
        wrappers: ['default-wrapper'],
        defaultValue: currentValue.age,
        props: { type: 'number', required: true },
        expressions: { 'props.label': translateService.stream('fields.age') }
      },
      {
        key: 'unit',
        type: 'input',
        wrappers: ['default-wrapper'],
        defaultValue: currentValue.unit || 'MPa',
        props: { required: true },
        expressions: { 'props.label': translateService.stream('fields.unit') }
      },
      {
        key: 'project',
        type: 'select',
        wrappers: ['default-wrapper'],
        defaultValue: currentValue.project?._id || currentValue.project,
        props: { options: projects, required: true },
        expressions: { 'props.label': translateService.stream('fields.project') }
      },
      {
        key: 'concrete',
        type: 'select',
        wrappers: ['default-wrapper'],
        defaultValue: currentValue.concrete?._id || currentValue.concrete,
        props: { options: concretes, required: true },
        expressions: { 'props.label': translateService.stream('fields.concrete') }
      }
    ];
  }
}

/**
 * Form for creating Control Standards
 */
export class AdminCreateStandardForm {
  fields: FormlyFieldConfig[];
  actionText = 'button.create';

  constructor(translateService) {
    this.fields = [
      {
        key: 'code',
        type: 'input',
        wrappers: ['default-wrapper'],
        props: { required: true },
        expressions: { 'props.label': translateService.stream('fields.code') }
      },
      {
        key: 'name',
        type: 'input',
        wrappers: ['default-wrapper'],
        props: { required: true },
        expressions: { 'props.label': translateService.stream('fields.name') }
      },
      {
        key: 'country',
        type: 'input',
        wrappers: ['default-wrapper'],
        props: { maxlength: 2 },
        expressions: { 'props.label': translateService.stream('fields.country') }
      },
      {
        key: 'acceptanceStrategy',
        type: 'input',
        wrappers: ['default-wrapper'],
        props: { required: true },
        expressions: { 'props.label': translateService.stream('fields.acceptanceStrategy') }
      },
      {
        key: 'usesConcreteCertification',
        type: 'checkbox',
        wrappers: ['checkbox-wrapper'],
        expressions: { 'props.label': translateService.stream('fields.usesConcreteCertification') }
      }
    ];
  }
}

/**
 * Form for editing Control Standards
 */
export class AdminEditStandardForm {
  fields: FormlyFieldConfig[];
  actionText = 'button.save';

  constructor(translateService, currentValue) {
    this.fields = [
      {
        key: 'code',
        type: 'input',
        wrappers: ['default-wrapper'],
        defaultValue: currentValue.code,
        props: { required: true },
        expressions: { 'props.label': translateService.stream('fields.code') }
      },
      {
        key: 'name',
        type: 'input',
        wrappers: ['default-wrapper'],
        defaultValue: currentValue.name,
        props: { required: true },
        expressions: { 'props.label': translateService.stream('fields.name') }
      },
      {
        key: 'country',
        type: 'input',
        wrappers: ['default-wrapper'],
        defaultValue: currentValue.country,
        props: { maxlength: 2 },
        expressions: { 'props.label': translateService.stream('fields.country') }
      },
      {
        key: 'acceptanceStrategy',
        type: 'input',
        wrappers: ['default-wrapper'],
        defaultValue: currentValue.acceptanceStrategy,
        props: { required: true },
        expressions: { 'props.label': translateService.stream('fields.acceptanceStrategy') }
      },
      {
        key: 'usesConcreteCertification',
        type: 'checkbox',
        wrappers: ['checkbox-wrapper'],
        defaultValue: currentValue.usesConcreteCertification,
        expressions: { 'props.label': translateService.stream('fields.usesConcreteCertification') }
      }
    ];
  }
}
